import { Button, makeStyles, SearchBox } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useGridState } from '../../hooks/useGridState';
import { useSearchTable } from '../../hooks/useSearchTable';

export interface IGridSearchBarProps {
    searchBoxPlaceholderText: string;
    stackStyle?: CSSProperties;
    buttonStyleOverride?: CSSProperties;
    disabled?: boolean;
}

const useStyles = makeStyles({
    stack: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '5px',
    },
    searchBox: {
        width: '250px',
        minWidth: '250px',
        maxWidth: '250px',
    },
});

const GridSearchBar: React.FC<IGridSearchBarProps> = ({ searchBoxPlaceholderText, stackStyle, buttonStyleOverride, disabled = false }) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { applySearch, clearSearch, setSearchValue, searchValue } = useSearchTable();
    const [isSearchboxFocused, { setTrue: setIsSearchboxFocusedTrue, setFalse: setIsSearchboxFocusedFalse }] = useBoolean(false);
    const { grid } = useGridState();

    const handleSearch = () => {
        if (disabled) {
            return;
        }
        applySearch(grid);
    };

    const handleChange = (_ev: unknown, data: { value: string }) => {
        setSearchValue(data.value);
    };

    const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        if (ev.key === 'Enter') {
            handleSearch();
        }
    };

    const handleDismissClick = () => {
        clearSearch(grid);
    };

    return (
        <div className={styles.stack} style={stackStyle}>
            <SearchBox
                value={searchValue}
                className={styles.searchBox}
                placeholder={isSearchboxFocused ? '' : searchBoxPlaceholderText}
                aria-label={searchBoxPlaceholderText}
                onChange={handleChange}
                onFocus={setIsSearchboxFocusedTrue}
                onBlur={setIsSearchboxFocusedFalse}
                onKeyDown={handleKeyDown}
                dismiss={{ onClick: handleDismissClick }}
                disabled={disabled}
            />
            <Button
                appearance='primary'
                aria-label={t('search').toString()}
                onClick={handleSearch}
                role='button'
                id='gridSearchButton'
                data-testid='grid-search-button'
                style={buttonStyleOverride}
                disabled={disabled}
                disabledFocusable={disabled}
            >
                {t('search').toString()}
            </Button>
        </div>
    );
};

export default GridSearchBar;
